// src/pages/VehicleV2/PeriodicEntry.jsx
// Design aligned with ProductionBOMListing
// Flow:
// 1) Page loads -> init API loads branches / stores / vehicle types / subtypes
// 2) Default branch/store applied from init
// 3) Search -> list API
// 4) View All / Clear -> reset search fields and reload
// 5) Details -> navigate to MaintenanceListing
//
// Backend:
//   app.use("/api/periodicEntry", periodicEntry);

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaEye,
} from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";
const API_BASE = `${BASE_URL}/api/periodicEntry`;

const getLS = (key, fallback = "") => {
  if (typeof window === "undefined") return fallback;
  const val = localStorage.getItem(key);
  return val !== null && val !== undefined ? val : fallback;
};

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const clean = (v) => String(v ?? "").trim();

const emptySearchFields = {
  vehicleTypeIndex: "",
  vehicleSubtypeId: "",
  vehicleCode: "",
  vehicleName: "",
  vehicleNo: "",
};

const PeriodicEntry = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("Branchid") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId") || getLS("userid");

  const branchId = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branchType = useMemo(() => toStr(branchTypeLS || "HO"), [branchTypeLS]);
  const userId = useMemo(() => toStr(userIdLS), [userIdLS]);
  const loginUserId = useMemo(() => {
    const sid =
      typeof window !== "undefined" ? sessionStorage.getItem("loginUserId") : "";
    return toStr(sid || userId);
  }, [userId]);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const [showFilters, setShowFilters] = useState(true);

  const [branches, setBranches] = useState([]);
  const [stores, setStores] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [vehicleSubtypes, setVehicleSubtypes] = useState([]);

  const [filters, setFilters] = useState({
    branchSelect: "",
    storeId: "",
    ...emptySearchFields,
  });

  const [list, setList] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  const [pageLoading, setPageLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);

  const isBusy = pageLoading || listLoading;

  const totalPages = useMemo(() => {
    const t = Math.ceil((total || 0) / limit);
    return t <= 0 ? 1 : t;
  }, [total, limit]);

  const fieldClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const renderText = useCallback((row, keys, fallback = "-") => {
    for (const k of keys) {
      const v = row?.[k];
      if (v !== undefined && v !== null && String(v).trim() !== "") {
        return String(v);
      }
    }
    return fallback;
  }, []);

  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: { loginUserId, branchId, branchType },
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Init failed");
        return;
      }

      setBranches(data?.branches || []);
      setStores(data?.stores || []);
      setVehicleTypes(data?.vehicleTypes || []);
      setVehicleSubtypes(data?.vehicleSubtypes || []);

      const nextBranchSelect = toStr(data?.branchId) || "";
      const nextStoreId = toStr(data?.defaultStoreId) || "";

      setFilters((prev) => ({
        ...prev,
        branchSelect: nextBranchSelect || prev.branchSelect,
        storeId: nextStoreId || prev.storeId,
      }));
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e.message || "Init failed");
    } finally {
      setPageLoading(false);
    }
  }, [loginUserId, branchId, branchType, commonHeaders]);

  const loadStoresByBranch = useCallback(
    async (selectedBranchId) => {
      try {
        setListLoading(true);

        const { data } = await axios.get(`${API_BASE}/stores`, {
          params: { branchId: selectedBranchId },
          headers: commonHeaders,
        });

        if (!data?.success) {
          toast.error(data?.message || "Store load failed");
          return;
        }

        const nextStores = data?.stores || [];
        setStores(nextStores);
        setFilters((prev) => ({
          ...prev,
          storeId: nextStores?.[0]?.id ? String(nextStores[0].id) : "",
        }));
      } catch (e) {
        console.error(e);
        toast.error(e?.response?.data?.message || e.message || "Store load failed");
      } finally {
        setListLoading(false);
      }
    },
    [commonHeaders]
  );

  const loadList = useCallback(
    async (pageNo = 1, overrideFilters = null) => {
      const f = overrideFilters || filters;

      if (!branchId) {
        toast.error("BranchId missing!");
        return;
      }

      try {
        setListLoading(true);
        setHasSearched(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: {
            loginUserId,
            branchId,
            branchType,
            branchSelect: clean(f.branchSelect),
            storeId: clean(f.storeId),
            vehicleTypeIndex: clean(f.vehicleTypeIndex),
            vehicleSubtypeId: clean(f.vehicleSubtypeId),
            vehicleCode: clean(f.vehicleCode),
            vehicleName: clean(f.vehicleName),
            vehicleNo: clean(f.vehicleNo),
            page: pageNo,
            limit,
          },
          headers: commonHeaders,
        });

        if (!data?.success) {
          setList([]);
          setTotal(0);
          toast.error(data?.message || "List fetch failed");
          return;
        }

        const rows = data?.list || [];
        setList(rows);
        setTotal(toInt(data?.total, 0));
        setPage(toInt(data?.page, pageNo));
        if (rows.length === 0) toast.info("No Records found");
      } catch (e) {
        console.error(e);
        setList([]);
        setTotal(0);
        toast.error(e?.response?.data?.message || e.message || "List fetch failed");
      } finally {
        setListLoading(false);
      }
    },
    [filters, loginUserId, branchId, branchType, limit, commonHeaders]
  );

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    if (!filters.branchSelect) return;
    if (!filters.storeId) return;
    loadList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.branchSelect, filters.storeId]);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      if (name === "branchSelect") {
        setFilters((prev) => ({
          ...prev,
          branchSelect: value,
          storeId: "",
        }));
        if (value && value !== "Select") {
          loadStoresByBranch(value);
        } else {
          setStores([]);
        }
        return;
      }

      setFilters((prev) => ({ ...prev, [name]: value }));
    },
    [loadStoresByBranch]
  );

  const handleSearch = useCallback(async () => {
    await loadList(1);
  }, [loadList]);

  const handleClearFilters = useCallback(async () => {
    const reset = {
      ...filters,
      ...emptySearchFields,
    };
    setFilters(reset);
    await loadList(1, reset);
  }, [filters, loadList]);

  const handlePage = useCallback(
    async (p) => {
      if (p < 1 || p > totalPages || listLoading) return;
      await loadList(p);
    },
    [totalPages, listLoading, loadList]
  );

  const handleDetails = useCallback(
    (row) => {
      navigate(`/vehicle/MaintenanceListing?vehicleId=${row.fld_vehicleid}`);
    },
    [navigate]
  );

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PERIODIC ENTRY</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Periodic Entry
            </h2>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  <button
                    onClick={handleClearFilters}
                    disabled={isBusy}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={handleSearch}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    {listLoading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branchSelect"
                    value={filters.branchSelect}
                    onChange={handleChange}
                    className={fieldClass}
                    disabled={isBusy}
                  >
                    <option value="">Select</option>
                    {branches.map((b) => (
                      <option key={b.id} value={String(b.id)}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    name="storeId"
                    value={filters.storeId}
                    onChange={handleChange}
                    className={fieldClass}
                    disabled={isBusy}
                  >
                    <option value="">Select</option>
                    {stores.map((s) => (
                      <option key={s.id} value={String(s.id)}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select
                    name="vehicleTypeIndex"
                    value={filters.vehicleTypeIndex}
                    onChange={handleChange}
                    className={fieldClass}
                    disabled={isBusy}
                  >
                    <option value="">Select</option>
                    {vehicleTypes.map((t, idx) => (
                      <option
                        key={t.vehicleTypeIndex ?? t.id ?? idx}
                        value={String(t.vehicleTypeIndex ?? t.id ?? "")}
                      >
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Subtype</label>
                  <select
                    name="vehicleSubtypeId"
                    value={filters.vehicleSubtypeId}
                    onChange={handleChange}
                    className={fieldClass}
                    disabled={isBusy}
                  >
                    <option value="">Select</option>
                    {vehicleSubtypes.map((st) => (
                      <option key={st.id} value={String(st.id)}>
                        {st.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    name="vehicleCode"
                    value={filters.vehicleCode}
                    onChange={handleChange}
                    className={fieldClass}
                    placeholder="Enter vehicle code"
                    disabled={isBusy}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Number</label>
                  <input
                    type="text"
                    name="vehicleNo"
                    value={filters.vehicleNo}
                    onChange={handleChange}
                    className={fieldClass}
                    placeholder="Enter vehicle number"
                    disabled={isBusy}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    name="vehicleName"
                    value={filters.vehicleName}
                    onChange={handleChange}
                    className={fieldClass}
                    placeholder="Enter vehicle name"
                    disabled={isBusy}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {pageLoading || listLoading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </div>
              </div>
            ) : list.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {hasSearched ? "No Records found" : "Please search to view records"}
                </div>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Main Type</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Code</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Number</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Type</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Company Name</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">Details</th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {list.map((row, idx) => (
                    <tr
                      key={row.id ?? row.fld_vehicleid ?? idx}
                      className={`${
                        idx % 2 === 0
                          ? isDark
                            ? "bg-gray-800"
                            : "bg-white"
                          : isDark
                          ? "bg-gray-750"
                          : "bg-gray-50"
                      } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                    >
                      <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                        {(page - 1) * limit + idx + 1}
                      </td>
                      <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                        {renderText(row, ["VehicleType", "vehicleMainType", "mainType"])}
                      </td>
                      <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                        {renderText(row, ["fld_vehicle_code", "vehicleCode", "code"])}
                      </td>
                      <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                        {renderText(row, ["fld_vehicle_regno", "vehicleRegNo", "number"])}
                      </td>
                      <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                        {renderText(row, ["fld_vehicle_name", "vehicleName", "name"])}
                      </td>
                      <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                        {renderText(row, ["vehicleMainType", "fld_vehicletypename", "type"])}
                      </td>
                      <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                        {renderText(row, ["fld_companyname", "companyName", "company"], "-")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          type="button"
                          onClick={() => handleDetails(row)}
                          className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition shadow-md"
                          title="Details"
                        >
                          <FaEye />
                          Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="flex justify-center mt-6">
            <nav className="flex items-center space-x-2">
              <button
                className={`px-3 py-1 border rounded ${
                  isDark
                    ? "bg-gray-800 text-white border-gray-700"
                    : "bg-white text-gray-700 border-gray-300"
                } ${page <= 1 || isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                onClick={() => handlePage(page - 1)}
                disabled={page <= 1 || isBusy}
              >
                Prev
              </button>
              <span className={`px-3 py-1 ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                Page {page} / {totalPages}
              </span>
              <button
                className={`px-3 py-1 border rounded ${
                  isDark
                    ? "bg-gray-800 text-white border-gray-700"
                    : "bg-white text-gray-700 border-gray-300"
                } ${page >= totalPages || isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                onClick={() => handlePage(page + 1)}
                disabled={page >= totalPages || isBusy}
              >
                Next
              </button>
            </nav>
          </div>

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default PeriodicEntry;